import {Box, Grid, VStack} from 'platform/foundation';

import i18n from '@dms/i18n';

import {type RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useAbcXyzDetailView} from '../hooks/useAbcXyzDetailView';
import type {AbcXyzTabProps} from '../types/AbcXyzTabProps';
import {AbcXyzClassCard} from './AbcXyzClassCard';
import {AbcXyzDetailGrid} from './AbcXyzDetailGrid';
import {AbcXyzIssuedQuantityChart} from './AbcXyzIssuedQuantityChart';
import {AbcXyzMetricsCard} from './AbcXyzMetricsCard';
import {AbcXyzThresholdsTable} from './AbcXyzThresholdsTable';

const XYZ_GRID_CODE = 'report-xyz-calculation-details';
const TOP_COLUMNS = 3;
const SUMMARY_MIN_HEIGHT_STEPS = 64;
/** Keep several rows visible when a short viewport requires scrolling the dialog. */
const GRID_MIN_HEIGHT_STEPS = 48;

export function AbcXyzXyzTab(props: AbcXyzTabProps & RequiredTestIdProps) {
  const view = useAbcXyzDetailView(props.detail);

  return (
    <VStack spacing={2} height={['auto', 'auto', '100%']} minHeight={0}>
      <Grid columns={[1, 1, TOP_COLUMNS]} spacing={2} minHeight={SUMMARY_MIN_HEIGHT_STEPS}>
        <AbcXyzMetricsCard
          title={i18n.t('entity.warehouse.reporting.keyFigures')}
          metrics={view.xyzMetrics}
          data-testid={suffixTestId('keyFigures', props)}
        />
        <Box height={[SUMMARY_MIN_HEIGHT_STEPS, SUMMARY_MIN_HEIGHT_STEPS, '100%']}>
          <AbcXyzIssuedQuantityChart
            detail={props.detail}
            title={i18n.t('entity.warehouse.reporting.monthlyVariabilityChartTitle')}
            isFullHeight
            data-testid={suffixTestId('chart', props)}
          />
        </Box>
        <VStack spacing={2}>
          <AbcXyzClassCard
            title={i18n.t('entity.warehouse.reporting.xyzClass')}
            classLetter={props.detail.xyzClass}
            name={view.xyzName}
            hint={view.xyzHint}
            colorScheme="purple"
            data-testid={suffixTestId('xyzClassCard', props)}
          />
          <AbcXyzThresholdsTable
            title={i18n.t('entity.warehouse.reporting.xyzThresholdsTitle')}
            thresholds={view.xyzThresholds}
            data-testid={suffixTestId('thresholds', props)}
          />
        </VStack>
      </Grid>
      <Box
        flex={[undefined, undefined, 1]}
        height={[GRID_MIN_HEIGHT_STEPS, GRID_MIN_HEIGHT_STEPS, undefined]}
        minHeight={GRID_MIN_HEIGHT_STEPS}
      >
        <AbcXyzDetailGrid
          gridCode={XYZ_GRID_CODE}
          articleId={props.articleId}
          data-testid={suffixTestId('xyzGrid', props)}
        />
      </Box>
    </VStack>
  );
}
